import {AbstractControl, ValidatorFn, Validators} from "@angular/forms";
import {ValidationErrors} from "@angular/forms";
import {Observable, timer, of} from "rxjs";
import {SupportService} from "../../support/support/support.service";
import {catchError, map, switchMap} from "rxjs/operators";
import {Injectable} from "@angular/core";
import * as moment from 'moment-timezone';
import {DATE_FORMAT} from "../constant";
import { HttpErrorResponse } from "@angular/common/http";

interface ErrorWithI18n {
	value: any;
	message: string;
	i18n: string;
	params: any;
}

interface ValidationErrorsWithI18n extends ValidationErrors {
	[key: string]: ErrorWithI18n;
}

const RequiredValidator = (message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		const error = Validators.required(control);
		if ( ! error) {
			return null;
		}
		
		return {
			'required': {
				value   : control.value,
				i18n    : 'validator error.required',
				message : message,
				params  : null,
			}
		};
	}
};

const MinLengthValidator = (minLength: number, message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		const error = Validators.minLength(minLength)(control);
		if ( ! error) {
			return null;
		}
		
		return {
			'minLength': {
				value   : control.value,
				i18n    : 'validator error.minLength',
				message : message,
				params  : [minLength],
			}
		};
	}
};

const PatternValidator = (pattern: string | RegExp, message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		const error = Validators.pattern(pattern)(control);
		if ( ! error) {
			return null;
		}
		
		return {
			'pattern': {
				value   : control.value,
				i18n    : 'validator error.pattern',
				message : message,
				params  : [pattern],
			}
		};
	}
};


const JSONValidator = (message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		try {
			JSON.parse(control.value);
			
			return null;
		} catch (err) {
			return {
				'json': {
					value   : control.value,
					i18n    : 'validator error.json',
					message : message,
					params  : [control.value],
				}
			}
		}
	}
};


const EmailValidator = (message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		const error = Validators.email(control);
		if ( ! error) {
			return null;
		}
		
		return {
			'email': {
				value   : control.value,
				i18n    : 'validator error.email',
				message : message,
				params  : null,
			}
		};
	}
};


const LteValidator = (lt: number, message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		if (control.value <= lt) {
			return null;
		}
		
		return {
			'lte': {
				value   : control.value,
				i18n    : 'validator error.lte',
				message : message,
				params  : [lt],
			}
		};
	}
};

const GteValidator = (gt: number, message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		if (control.value >= gt) {
			return null;
		}
		
		return {
			'gte': {
				value   : control.value,
				i18n    : 'validator error.gte',
				message : message,
				params  : [gt],
			}
		};
	}
};

const DateValidator = (format: string = DATE_FORMAT, message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		if (moment(control.value, [format, 'YYYY-MM-DDTHH:mm:ss.SSSZ'], true).isValid()) {
			return null;
		}
		
		return {
			'date': {
				value   : control.value,
				i18n    : 'validator error.date',
				message : message,
				params  : [control.value || '', format],
			}
		};
	}
};


const OnlyASCIValidator = (message?: string): ValidatorFn => {
	return (control: AbstractControl): ValidationErrorsWithI18n | null => {
		//TODO Переосмыслить данную валидацию. Например ещё знак минус/тире нужен в mark для промокода
		return null;

		if (/^\w+$/.test(control.value)) {
			return null;
		}

		return {
			'onlyASCI': {
				value   : control.value,
				i18n    : 'validator error.only ascii chars',
				message : message,
				params  : null,
			}
		};
	}
};


const AsyncFnValidator = (fn: Function, name: string, message?: string) => {
	return (control: AbstractControl): Observable<ValidationErrors | null> => {
		if ( ! control.value) {
			return of(null);
		}
		
		return timer(2000).pipe(
			switchMap(
				() => fn(control.value).pipe(
					map(err => {
						if (err) {
							const errors = {
								[name]: {
									value   : control.value,
									i18n    : err instanceof Error ? err.message : err.toString(),
									message : message,
									params  : null,
								}
							};
							
							control.setErrors(errors);
							
							return errors;
						}
						
						control.setErrors(null);
						
						return null;
					}),
				)
			)
		);
	};
}


@Injectable()
export class FormControlASyncValidators {
	constructor(
		public supportService: SupportService,
	) {
	
	}
	
	emailExists(message?: string) {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			return timer(2000).pipe(
				switchMap(
					() => this.supportService.searchByEmail(control.value).pipe(
						map(data => {
							if (data) {
								
								// Это нужно для того, чтобы директива formErrorIndicator
								// отловила событие statusChanges и убрала спиннер
								control.setErrors(null);
								return null;
							}
							
							return {
								'emailExists': {
									value   : control.value,
									i18n    : 'validator error.email is not found',
									message : message,
									params  : null,
								}
							};
						}),
						catchError(() => {
							return of({
								emailExists: {
									value   : control.value,
									i18n    : 'validator error.email is not found',
									message : message,
									params  : null,
								}
							});
						})
					)
				)
			);
		};
	}
	
	
	asyncFn(fn: Function, name: string, message?: string) {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			if ( ! control.value) {
				return of(null);
			}
			
			return timer(2000).pipe(
				switchMap(
					() => fn(control.value).pipe(
						map(err => {
							if (err) {
								const errors = {
									[name]: {
										value   : control.value,
										i18n    : err instanceof Error ? err.message : err.toString(),
										message : message,
										params  : null,
									}
								};
								
								control.setErrors(errors);
								
								return errors;
							}
							
							control.setErrors(null);
							
							return null;
						}),
					)
				)
			);
		};
	}
}

export class FormControlValidators {
	static required(message?: string) {
		return RequiredValidator(message);
	}
	
	static minLength(minLength: number, message?: string) {
		return MinLengthValidator(minLength, message);
	}
	
	static pattern(pattern: string | RegExp, message?: string) {
		return PatternValidator(pattern, message);
	}
	
	static json(message?: string) {
		return JSONValidator(message);
	}

	static email(message?: string) {
		return EmailValidator(message);
	}
	
	static lte(lt: number, message?: string) {
		return LteValidator(lt, message);
	}
	
	static date(format = DATE_FORMAT, message?: string) {
		return DateValidator(format, message);
	}
	
	static gte(lt: number, message?: string) {
		return GteValidator(lt, message);
	}
	
	static onlyASCII(message?: string) {
		return OnlyASCIValidator(message);
	}
	
	static asyncFn(fn, name, message?: string) {
		return AsyncFnValidator(fn, name, message);
	}
}
